import graphqlSpec from './graphql-spec.json';

// Keep the dangle as it matches the GraphQL spec format
// eslint-disable-next-line no-underscore-dangle
const schemaTypes = graphqlSpec.data.__schema.types;

export type GraphQLType =
  | 'String'
  | 'Enum'
  | 'EnumList'
  | 'Boolean'
  | 'DateTime'
  | 'TimePeriod'
  | 'Int'
  | 'BigInt'
  | 'Float';
export type GraphQLField = {
  defaultValue: any;
  description?: string;
  isDeprecated?: boolean;
  deprecationReason?: string;
  type: GraphQLType;
  enumValues?: any[];
};
export type GraphQLFieldList = {
  [key: string]: GraphQLField;
};
const specClient = (() => {
  // Generate easy lookup of types by name (many are shared by multiple queries)
  const types: Record<string, any> = schemaTypes.reduce((map, type) => {
    map[type.name] = type;
    return map;
  }, {});

  // Utility to recursively dig deeper into fields that hold objects or lists
  const getAllFields = (
    parentType: string,
    key = '',
    base: Record<string, any> = {},
  ): GraphQLFieldList =>
    (types[parentType].kind === 'INPUT_OBJECT'
      ? types[parentType].inputFields
      : types[parentType].fields
    ).reduce((fields, field) => {
      const {
        defaultValue,
        description,
        name: fieldName,
        type,
        isDeprecated,
        deprecationReason,
      } = field;
      const fieldKey = key ? `${key}.${fieldName}` : fieldName;
      const { name, kind, ofType } = type;
      // If it's an input object, recurse deeper to get the fields within
      if (
        kind === 'INPUT_OBJECT' ||
        kind === 'OBJECT' ||
        (kind === 'LIST' &&
          (ofType.kind === 'INPUT_OBJECT' || ofType.kind === 'OBJECT'))
      ) {
        return getAllFields(name || ofType.name, fieldKey, fields);
      }
      // Otherwise,this is a leaf node and we can add the field
      let typeName: string = name;
      const isEnumType =
        (kind === 'LIST' && ofType.kind === 'ENUM') || kind === 'ENUM';
      if (isEnumType) {
        typeName = `Enum${kind === 'LIST' ? 'List' : ''}`;
      }
      fields[fieldKey] = {
        // Boolean fields should never be null, because there's no way to distinguish between false and null in a toggle
        defaultValue: (defaultValue ?? typeName === 'Boolean') ? false : null,
        isDeprecated,
        deprecationReason,
        description,
        type: typeName,
        ...(isEnumType
          ? { enumValues: types[type.name || ofType.name].enumValues }
          : {}),
      };
      return fields;
    }, base);

  // Generate easy lookup of queries by name
  const queries: {
    [queryName: string]: {
      name: string;
      filterType: string | null;
      filters: GraphQLFieldList;
      fields: GraphQLFieldList;
    };
  } = types.Query.fields.reduce((acc, query) => {
    const { name, args, type } = query;
    const responseType = types[type.name].fields.find(
      field => field.name === 'results',
    ).type.ofType.name;
    const hasFilter = args.find(arg => arg.name === 'filter');
    acc[name] = {
      name,
      filterType: hasFilter ? hasFilter.type.name : null,
      filters: hasFilter ? getAllFields(hasFilter.type.name) : {},
      fields: getAllFields(responseType),
    };
    return acc;
  }, {});

  const getAllQueries = () => queries;
  const getQuery = (queryName: string) =>
    queryName in queries ? queries[queryName] : undefined;
  const getQueryFilterType = (queryName: string) =>
    getQuery(queryName)?.filterType;
  const getQueryFilters = (queryName: string) => getQuery(queryName)?.filters;
  const getQueryFiltersCount = (queryName: string) =>
    Object.keys(getQueryFilters(queryName) || {}).length;
  const getQueryFields = (queryName: string) => getQuery(queryName)?.fields;
  const isValidQueryName = (name: string) => name in queries;

  return {
    getAllQueries,
    getQuery,
    getQueryFilterType,
    getQueryFilters,
    getQueryFiltersCount,
    getQueryFields,
    isValidQueryName,
  };
})();

export const {
  getAllQueries,
  getQuery,
  getQueryFilterType,
  getQueryFilters,
  getQueryFiltersCount,
  getQueryFields,
  isValidQueryName,
} = specClient;
